import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const Navbar = () => {
    const navigate = useNavigate();
    
    // --- STATE ---
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    
    // Password Change State
    const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passMessage, setPassMessage] = useState({ type: '', text: '' });
    const [isLoading, setIsLoading] = useState(false);

    // Get user from local storage safely
    const userString = localStorage.getItem('user');
    const user = userString ? JSON.parse(userString) : null;

    // If no user is found, don't render the navbar (they shouldn't be here anyway)
    if (!user) return null;

    // --- HANDLERS ---
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/auth'; // Hard redirect to clear everything
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPassMessage({ type: '', text: '' });

        if (passwords.newPassword !== passwords.confirmPassword) {
            return setPassMessage({ type: 'error', text: 'New passwords do not match!' });
        }
        if (passwords.newPassword.length < 6) {
            return setPassMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
        }

        setIsLoading(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword
            });
            setPassMessage({ type: 'success', text: 'Password updated successfully!' });
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => setIsSettingsOpen(false), 2000); // Close modal after 2 seconds
        } catch (err) {
            setPassMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update password.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* --- STICKY TOP NAVBAR --- */}
            <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm w-full">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16 items-center">
                        
                        {/* LEFT SIDE: Logo / Brand */}
                        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
                                <span className="text-white font-black text-xl leading-none">T</span>
                            </div>
                            <span className="font-black text-xl tracking-tight text-gray-900 hidden sm:block">Tournament Master</span>
                        </div>

                        {/* RIGHT SIDE: Profile Dropdown */}
                        <div className="relative">
                            <button 
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-50 transition-colors focus:outline-none"
                            >
                                <div className="flex flex-col text-right sm:block">
                                    <span className="text-sm font-bold text-gray-900 leading-none">{user.username}</span>
                                    <span className="text-xs font-bold text-blue-600 uppercase mt-1">{user.role}</span>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-purple-600 flex items-center justify-center text-white font-black text-lg shadow-md border-2 border-white">
                                    {user.username.charAt(0).toUpperCase()}
                                </div>
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                                <>
                                    {/* Invisible overlay to close dropdown when clicking outside */}
                                    <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)}></div>
                                    
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                        <div className="p-4 border-b border-gray-50 bg-gray-50/50 sm:hidden">
                                            <div className="font-bold text-gray-900">{user.username}</div>
                                            <div className="text-xs font-bold text-blue-600 uppercase">{user.role}</div>
                                        </div>
                                        
                                        {user.role === 'admin' && (
                                            <button onClick={() => { setIsDropdownOpen(false); navigate('/admin'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors flex items-center border-b border-gray-50">
                                                <span className="text-purple-600 mr-2">⚡</span> Admin Panel
                                            </button>
                                        )}
                                        
                                        <button onClick={() => { setIsDropdownOpen(false); setIsSettingsOpen(true); }} className="w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors">
                                            Account Settings
                                        </button>
                                        <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors">
                                            Log Out
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* --- SETTINGS / CHANGE PASSWORD MODAL --- */}
            {isSettingsOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 pb-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-2xl font-black text-gray-900 tracking-tight">Account Settings</h3>
                            <button onClick={() => setIsSettingsOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>

                        <div className="p-6">
                            <h4 className="font-bold text-sm text-gray-500 uppercase tracking-wider mb-4">Change Password</h4>
                            
                            {passMessage.text && (
                                <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-bold text-center ${passMessage.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                                    {passMessage.text}
                                </div>
                            )}

                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 ml-1">Current Password</label>
                                    <input type="password" required value={passwords.currentPassword} onChange={e => setPasswords({...passwords, currentPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 ml-1">New Password</label>
                                    <input type="password" required value={passwords.newPassword} onChange={e => setPasswords({...passwords, newPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 ml-1">Confirm New Password</label>
                                    <input type="password" required value={passwords.confirmPassword} onChange={e => setPasswords({...passwords, confirmPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"/>
                                </div>
                                <button type="submit" disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-black text-sm uppercase tracking-wide shadow-lg shadow-blue-100 transition-all active:scale-95 disabled:opacity-70 mt-2">
                                    {isLoading ? 'Updating...' : 'Update Password'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;